/*
 * The three Bootstrap JavaScript behaviours this app actually used: dropdown, tab and tooltip.
 *
 * Bootstrap's bundle is 80KB to provide seventeen components, of which the templates referenced
 * three, across fourteen elements. This is those three.
 *
 * Everything is delegated from `document` rather than instantiated per element. Bootstrap needed
 * an `initPopover()` / `initTooltips()` pair re-run on `turbo:frame-load`, and any element Turbo
 * swapped in between those calls stayed dead; delegation has no initialisation step to miss, so a
 * dropdown rendered into a frame five minutes from now works for the same reason one present at
 * parse time does.
 *
 * The attributes are `data-wp-*`, not `data-bs-*`. Leaving Bootstrap's names behind after deleting
 * Bootstrap would have left the next reader hunting for a dependency that is not there.
 */
const DROPDOWN_TOGGLE = '[data-wp-toggle="dropdown"]';
const TAB_TOGGLE = '[data-wp-toggle="tab"]';
const TOOLTIP = '[data-wp-toggle="tooltip"]';

const TOOLTIP_GAP = 8;

/** The element an event landed on, when it landed on an element at all. */
function elementOf(event: Event): Element | null {
  return event.target instanceof Element ? event.target : null;
}

/* --------------------------------------------------------------------------- dropdown ---- */

function closeDropdowns(except?: Element): void {
  document.querySelectorAll(".dropdown-menu.show").forEach((menu) => {
    if (menu === except) {
      return;
    }

    menu.classList.remove("show");
    menu
      .closest(".dropdown")
      ?.querySelector(DROPDOWN_TOGGLE)
      ?.setAttribute("aria-expanded", "false");
  });
}

function toggleDropdown(toggle: Element): void {
  const menu = toggle.closest(".dropdown")?.querySelector(".dropdown-menu");
  if (menu === null || menu === undefined) {
    return;
  }

  const open = !menu.classList.contains("show");

  /* Close the others first: two open menus overlapping is never what was wanted, and Bootstrap
       behaved the same way. */
  closeDropdowns(menu);
  menu.classList.toggle("show", open);
  toggle.setAttribute("aria-expanded", String(open));
}

/* --------------------------------------------------------------------------- tabs -------- */

function activateTab(toggle: HTMLElement): void {
  const selector = toggle.dataset.wpTarget;
  const pane =
    selector === undefined || selector === ""
      ? null
      : document.querySelector(selector);
  if (pane === null) {
    return;
  }

  /* Scope to this tab list, so a page with two independent sets of tabs does not deactivate the
       other one. */
  const list: ParentNode = toggle.closest('.nav, [role="tablist"]') ?? document;
  list.querySelectorAll(TAB_TOGGLE).forEach((other) => {
    other.classList.toggle("active", other === toggle);
    other.setAttribute("aria-selected", String(other === toggle));
  });

  /* The panes are the direct children of the pane's own container, expressed as a filter over
       `children` rather than as `:scope > .tab-pane`. Same set of elements, and it does not depend
       on a selector feature the test environment happens not to implement. */
  Array.from(pane.parentElement?.children ?? [])
    .filter((other) => other.classList.contains("tab-pane"))
    .forEach((other) => {
      other.classList.toggle("active", other === pane);
    });
}

/* --------------------------------------------------------------------------- tooltip ----- */

let tooltipElement: HTMLElement | null = null;

function hideTooltip(): void {
  tooltipElement?.remove();
  tooltipElement = null;
}

function showTooltip(target: HTMLElement): void {
  /* `title` would otherwise also render as the browser's own native tooltip, giving two. Moving
       it to a data attribute is how Bootstrap avoided that too. */
  const text = target.dataset.wpTitle ?? target.getAttribute("title");
  if (text === null || text === "") {
    return;
  }

  if (target.hasAttribute("title")) {
    target.dataset.wpTitle = text;
    target.removeAttribute("title");
  }

  hideTooltip();

  const tooltip = document.createElement("div");
  tooltip.className = "wp-tooltip";
  tooltip.setAttribute("role", "tooltip");
  tooltip.textContent = text;
  document.body.appendChild(tooltip);
  tooltipElement = tooltip;

  /* Positioned after insertion, because the width is not knowable until it has been laid out.
       Clamped to the viewport so a tooltip on a right-edge element does not cause a horizontal
       scrollbar. */
  const anchor = target.getBoundingClientRect();
  const tip = tooltip.getBoundingClientRect();
  const left = Math.min(
    Math.max(TOOLTIP_GAP, anchor.left + anchor.width / 2 - tip.width / 2),
    document.documentElement.clientWidth - tip.width - TOOLTIP_GAP,
  );
  const above = anchor.top - tip.height - TOOLTIP_GAP;

  tooltip.style.left = `${left + window.scrollX}px`;
  tooltip.style.top = `${(above < 0 ? anchor.bottom + TOOLTIP_GAP : above) + window.scrollY}px`;
}

/* --------------------------------------------------------------------------- wiring ------ */

export default function registerBehaviour(): void {
  document.addEventListener("click", (event) => {
    const target = elementOf(event);
    if (target === null) {
      return;
    }

    const dropdown = target.closest(DROPDOWN_TOGGLE);
    if (dropdown !== null) {
      event.preventDefault();
      toggleDropdown(dropdown);

      return;
    }

    const tab = target.closest<HTMLElement>(TAB_TOGGLE);
    if (tab !== null) {
      event.preventDefault();
      activateTab(tab);

      return;
    }

    /* A click inside an open menu is a menu item being chosen, so it closes; a click anywhere
           else dismisses. Either way the menus go. */
    closeDropdowns();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeDropdowns();
      hideTooltip();
    }
  });

  /* `focusin`/`focusout` rather than `focus`/`blur`, which do not bubble and so cannot be
       delegated. This is what gives the tooltip keyboard parity. */
  (["focusin", "mouseover"] as const).forEach((name) => {
    document.addEventListener(name, (event) => {
      const target = elementOf(event)?.closest<HTMLElement>(TOOLTIP);
      if (target !== null && target !== undefined) {
        showTooltip(target);
      }
    });
  });

  (["focusout", "mouseout"] as const).forEach((name) => {
    document.addEventListener(name, (event) => {
      if (elementOf(event)?.closest(TOOLTIP) != null) {
        hideTooltip();
      }
    });
  });

  /* A tooltip is positioned absolutely against the document, so anything that moves the page out
       from under it leaves it stranded. */
  document.addEventListener("scroll", hideTooltip, { passive: true });
  document.addEventListener("turbo:before-render", () => {
    hideTooltip();
    closeDropdowns();
  });
}
